import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchWithAuth } from "@/lib/api/client";
import { showToast } from "@/lib/toast";

export interface MeasurementOut {
  id: string;
  document_id: string;
  filename: string | null;
  codigo: string | null;
  descripcion: string | null;
  cantidad: number | null;
  unidad: string | null;
  precio_unit: number | null;
  importe: number | null;
}

export interface MeasurementDocSummary {
  document_id: string;
  filename: string | null;
  partidas: number;
  con_cantidad: number;
  total_importe: number;
}

export interface MeasurementListOut {
  items: MeasurementOut[];
  count: number;
  total_importe: number;
  documentos_presupuesto: number;
  documentos: MeasurementDocSummary[];
}

export interface MeasurementExtractResult {
  procesados: number;
  presupuestos: number;
  partidas: number;
  omitidos_sin_texto: number;
}

export const measurementKeys = {
  list: (projectId: string) => ["measurements", "list", projectId] as const,
};

export function useMeasurements(projectId: string) {
  return useQuery<MeasurementListOut, Error>({
    queryKey: measurementKeys.list(projectId),
    queryFn: () => fetchWithAuth(`/api/v1/projects/${projectId}/measurements`),
    enabled: !!projectId,
  });
}

export function useExtractMeasurements(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<MeasurementExtractResult, Error, void>({
    mutationFn: () =>
      fetchWithAuth(`/api/v1/projects/${projectId}/measurements/extract`, {
        method: "POST",
      }),
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: measurementKeys.list(projectId) });
      showToast.success(
        `${r.partidas} partidas de ${r.presupuestos} presupuesto(s)`
      );
    },
    onError: (e) => showToast.error(e.message || "Error al extraer mediciones"),
  });
}
